/**
 * BuiltinVariablesRenderer - Start node built-in variables selection
 *
 * Allows users to enable/disable context variables like user_id, user_name, etc.
 * Note: This renderer does not support field mapping as it's for variable selection.
 */

import React from 'react';
import { Variable } from 'lucide-react';
import type { BuiltInVariables } from '@/types/imageWorkflow';
import type { BaseCustomRendererProps } from './types';

// Built-in variable definitions
const BUILTIN_VARIABLES: { key: keyof BuiltInVariables; label: string; description: string }[] = [
  { key: 'user_id', label: '用户 ID', description: '目标用户的唯一标识' },
  { key: 'user_name', label: '用户名', description: '目标用户的显示名称' },
  { key: 'user_email', label: '用户邮箱', description: '目标用户的邮箱地址' },
  { key: 'execution_id', label: '执行 ID', description: '当前执行的唯一标识' },
  { key: 'workflow_id', label: '工作流 ID', description: '当前工作流的唯一标识' },
  { key: 'workflow_name', label: '工作流名称', description: '当前工作流的名称' },
  { key: 'timestamp', label: '时间戳', description: '执行开始时间 (ISO 格式)' },
  { key: 'date', label: '日期', description: '执行日期 (YYYY-MM-DD)' },
  { key: 'time', label: '时间', description: '执行时间 (HH:mm:ss)' },
];

export const BuiltinVariablesRenderer: React.FC<BaseCustomRendererProps> = ({
  value,
  onChange,
  isDisabled = false,
}) => {
  const enabledBuiltins = (value as string[]) || [];

  const handleToggle = (key: keyof BuiltInVariables, checked: boolean) => {
    const updated = checked
      ? [...enabledBuiltins, key]
      : enabledBuiltins.filter((k) => k !== key);
    onChange(updated);
  };

  return (
    <div className="p-4 border border-cyan-200 rounded-lg bg-cyan-50/50 space-y-3">
      <div className="flex items-center gap-2">
        <Variable className="w-4 h-4 text-cyan-600" />
        <span className="text-sm font-semibold text-cyan-700">内置变量</span>
        <span className="text-xs text-cyan-500">选择要启用的变量</span>
      </div>
      <div className="grid grid-cols-1 gap-2">
        {BUILTIN_VARIABLES.map((variable) => {
          const isEnabled = enabledBuiltins.includes(variable.key);
          return (
            <label
              key={variable.key}
              className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                isEnabled ? 'bg-cyan-100' : 'bg-white hover:bg-gray-50'
              } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => handleToggle(variable.key, e.target.checked)}
                disabled={isDisabled}
                className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{variable.label}</span>
                  <code className="text-xs text-cyan-600 bg-cyan-100 px-1.5 py-0.5 rounded">
                    {`{{ctx.${variable.key}}}`}
                  </code>
                </div>
                <p className="text-xs text-gray-500 truncate">{variable.description}</p>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default BuiltinVariablesRenderer;
